// src/components/fx/DiwaliBanner.jsx
//
// "BIG Diwali Sale" festive banner — purple night sky, city lights,
// shop logo with rockets, ornate magenta frame with glowing bulbs,
// golden "UPTO 90% OFF" starburst, marigold garlands + swinging lantern,
// brass flower bowl, flickering diyas, rangoli, burning crackers and
// live fireworks.
//
//   variant="compact"  → Products page
//   variant="hero"     → Home page (taller, shows `children` as CTA row)

import React from 'react';
import FireworksCanvas from './FireworksCanvas';
import CrackerScene from './CrackerScene';
import { SHOP_NAME } from '../../config/tenant';
import './fx.css';
import './DiwaliBanner.css';

/* ── Ornate frame outline (viewBox 0 0 400 440) ─────────────── */
const FRAME = `M200 8 C214 30 236 40 262 42 C292 44 312 34 330 48 C348 62 372 58 384 76
L384 150 C396 160 396 176 384 186 L384 300 C396 310 396 326 384 336 L384 364
C372 382 348 378 330 392 C312 406 292 396 262 398 C236 400 214 410 200 432
C186 410 164 400 138 398 C108 396 88 406 70 392 C52 378 28 382 16 364
L16 336 C4 326 4 310 16 300 L16 186 C4 176 4 160 16 150 L16 76
C28 58 52 62 70 48 C88 34 108 44 138 42 C164 40 186 30 200 8 Z`;

/* ── Starburst points ───────────────────────────────────────── */
const STAR = (() => {
  const n = 18, cx = 110, cy = 110, ro = 104, ri = 86, pts = [];
  for (let i = 0; i < n * 2; i++) {
    const a = (Math.PI * i) / n - Math.PI / 2, r = i % 2 ? ri : ro;
    pts.push(`${(cx + Math.cos(a) * r).toFixed(1)},${(cy + Math.sin(a) * r).toFixed(1)}`);
  }
  return pts.join(' ');
})();

function Rocket({ x, y, rot, body, band }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <line x1="0" y1="0" x2="0" y2="70" stroke="#e8e0d0" strokeWidth="3" strokeDasharray="6 4" />
      <rect x="-9" y="-44" width="18" height="46" rx="3" fill={body} />
      <rect x="-9" y="-34" width="18" height="6" fill={band} />
      <rect x="-9" y="-20" width="18" height="6" fill={band} />
      <path d="M-10 -44 L10 -44 L0 -66 Z" fill={band} />
      <path d="M-9 -2 L-16 8 L-9 4 Z M9 -2 L16 8 L9 4 Z" fill={band} />
    </g>
  );
}

function BrandMark() {
  const [first, ...rest] = SHOP_NAME.split(' ');
  return (
    <div className="db-logo">
      <svg viewBox="0 0 260 210" className="db-logo-svg" aria-hidden="true">
        <defs>
          <linearGradient id="dbEmb" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7b2cbf" /><stop offset=".5" stopColor="#c2185b" /><stop offset=".5" stopColor="#2e7d32" /><stop offset="1" stopColor="#1b5e20" />
          </linearGradient>
        </defs>
        <Rocket x={90}  y={70} rot={-22} body="#3a86ff" band="#ffbe0b" />
        <Rocket x={130} y={56} rot={0}   body="#06d6a0" band="#ff006e" />
        <Rocket x={170} y={70} rot={22}  body="#ffbe0b" band="#3a86ff" />
        {[[40, 60, '#ffd60a'], [220, 64, '#06d6a0'], [60, 100, '#ff006e'], [206, 104, '#3a86ff'], [36, 130, '#ffbe0b']].map(([x, y, c], i) => (
          <path key={i} className="db-twinkle" style={{ animationDelay: `${i * .35}s` }}
            d={`M${x} ${y - 7} L${x + 2} ${y - 2} L${x + 7} ${y} L${x + 2} ${y + 2} L${x} ${y + 7} L${x - 2} ${y + 2} L${x - 7} ${y} L${x - 2} ${y - 2} Z`} fill={c} />
        ))}
        <circle cx="130" cy="138" r="62" fill="url(#dbEmb)" stroke="#ffd60a" strokeWidth="4" />
        <circle cx="130" cy="138" r="50" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="2" strokeDasharray="4 5" />
        <circle cx="130" cy="138" r="36" fill="none" stroke="rgba(255,214,10,.5)" strokeWidth="2" strokeDasharray="2 4" />
      </svg>
      <div className="db-logo-text">
        <span className="db-logo-main">{first}</span>
        {rest.length > 0 && <span className="db-logo-sub">{rest.join(' ')}</span>}
      </div>
    </div>
  );
}

function Skyline() {
  const windows = [];
  const blocks = [[20, 70, 50], [80, 50, 70], [160, 60, 44], [230, 40, 80], [300, 66, 56], [380, 46, 62], [440, 58, 48]];
  blocks.forEach(([x, w, hgt], b) => {
    for (let yy = 160 - hgt + 10; yy < 150; yy += 14) {
      for (let xx = x + 6; xx < x + w - 6; xx += 12) {
        if ((xx + yy + b) % 3) windows.push([xx, yy]);
      }
    }
  });
  return (
    <svg className="db-skyline" viewBox="0 0 520 160" preserveAspectRatio="xMinYMax slice" aria-hidden="true">
      {blocks.map(([x, w, hgt], i) => <rect key={i} x={x} y={160 - hgt} width={w} height={hgt} fill="#2a1450" />)}
      <path d="M150 104 Q175 70 200 104 Z M300 96 Q326 58 352 96 Z M246 80 Q270 44 294 80 Z" fill="#2a1450" />
      <rect x="268" y="30" width="4" height="18" fill="#2a1450" />
      {windows.map(([x, y], i) => (
        <rect key={i} x={x} y={y} width="5" height="6" rx="1" fill="#ffcf70"
          className={i % 7 === 0 ? 'db-win' : undefined} style={{ animationDelay: `${(i % 5) * .7}s` }} opacity=".85" />
      ))}
      {/* palms */}
      {[[10, 150], [500, 150]].map(([x, y], i) => (
        <g key={i} fill="#1a0b35">
          <path d={`M${x} ${y} Q${x + 4} ${y - 60} ${x + 2} ${y - 90} L${x + 6} ${y - 90} Q${x + 8} ${y - 60} ${x + 6} ${y} Z`} />
          <path d={`M${x + 4} ${y - 90} q-30 -4 -44 18 q22 -10 44 -14 q-24 -22 -8 -30 q10 16 8 26 q18 -24 40 -18 q-24 6 -36 20 q34 -6 44 16 q-24 -12 -48 -18 Z`} />
        </g>
      ))}
    </svg>
  );
}

function Garland() {
  const strings = [0, 26, 52];
  return (
    <svg className="db-garland" viewBox="0 0 90 300" aria-hidden="true">
      {strings.map((x, s) => (
        <g key={s}>
          {Array.from({ length: 11 - s * 2 }, (_, i) => (
            <circle key={i} cx={14 + x} cy={10 + i * 24} r="10.5"
              fill={i % 2 ? '#ffb703' : '#fb8500'} stroke="#e85d04" strokeWidth="1.5" />
          ))}
          {Array.from({ length: 10 - s * 2 }, (_, i) => (
            <ellipse key={'l' + i} cx={14 + x + (i % 2 ? 7 : -7)} cy={22 + i * 24} rx="5" ry="2.6" fill="#2d6a4f"
              transform={`rotate(${i % 2 ? -30 : 30} ${14 + x} ${22 + i * 24})`} />
          ))}
        </g>
      ))}
    </svg>
  );
}

function Lantern() {
  return (
    <svg className="db-lantern" viewBox="0 0 60 150" aria-hidden="true">
      <line x1="30" y1="0" x2="30" y2="40" stroke="#b8860b" strokeWidth="2" />
      <circle cx="30" cy="92" r="34" fill="rgba(255,190,80,.35)" className="db-flicker" />
      <path d="M18 44 L42 44 L46 54 L14 54 Z" fill="#b8860b" />
      <rect x="16" y="54" width="28" height="56" rx="6" fill="#ffcf70" stroke="#b8860b" strokeWidth="3" />
      <line x1="30" y1="54" x2="30" y2="110" stroke="#b8860b" strokeWidth="2" />
      <line x1="16" y1="82" x2="44" y2="82" stroke="#b8860b" strokeWidth="2" />
      <path d="M16 110 L44 110 L40 120 L20 120 Z" fill="#b8860b" />
      <circle cx="30" cy="126" r="3" fill="#b8860b" />
    </svg>
  );
}

function Diya({ className = '', style }) {
  return (
    <svg className={`db-diya ${className}`} style={style} viewBox="0 0 60 56" aria-hidden="true">
      <defs>
        <radialGradient id="dbFlameGlow"><stop offset="0" stopColor="#fff3b0" /><stop offset=".45" stopColor="rgba(255,170,40,.55)" /><stop offset="1" stopColor="rgba(255,120,0,0)" /></radialGradient>
        <linearGradient id="dbClay" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#e07a3f" /><stop offset="1" stopColor="#7a3212" /></linearGradient>
      </defs>
      <circle cx="30" cy="22" r="20" fill="url(#dbFlameGlow)" className="db-flicker" />
      <path d="M30 6 C35 16 36 22 30 28 C24 22 25 16 30 6 Z" fill="#ffd166" className="db-flame" />
      <path d="M30 13 C32 18 32 22 30 25 C28 22 28 18 30 13 Z" fill="#fff" className="db-flame" />
      <path d="M4 30 Q30 32 56 30 Q52 52 30 52 Q8 52 4 30 Z" fill="url(#dbClay)" />
      <path d="M4 30 Q30 36 56 30" fill="none" stroke="#ffb35c" strokeWidth="2" />
    </svg>
  );
}

function Bowl() {
  const flowers = [];
  const cols = ['#ff006e', '#ffbe0b', '#fb5607', '#ff4d6d', '#f72585', '#ffd60a'];
  for (let i = 0; i < 22; i++) flowers.push([20 + ((i * 37) % 180), 36 + ((i * 13) % 14), cols[i % cols.length]]);
  return (
    <div className="db-bowl">
      <svg viewBox="0 0 220 90" aria-hidden="true">
        <defs>
          <linearGradient id="dbBrass" x1="0" x2="1"><stop offset="0" stopColor="#7a5a14" /><stop offset=".45" stopColor="#f6d27a" /><stop offset="1" stopColor="#6b4c0f" /></linearGradient>
        </defs>
        <path d="M6 44 Q110 60 214 44 Q200 88 110 88 Q20 88 6 44 Z" fill="url(#dbBrass)" />
        <ellipse cx="110" cy="44" rx="104" ry="14" fill="#8a6418" />
        {flowers.map(([x, y, c], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="6" fill={c} />
            <circle cx={x} cy={y} r="2" fill="#fff3b0" />
          </g>
        ))}
      </svg>
    </div>
  );
}

function Rangoli() {
  const rings = [['#ff006e', 90], ['#ffbe0b', 74], ['#3a86ff', 58], ['#06d6a0', 42], ['#fb5607', 26], ['#fff', 10]];
  return (
    <svg className="db-rangoli" viewBox="0 0 200 200" aria-hidden="true">
      {rings.map(([c, r], i) => (
        <circle key={i} cx="100" cy="100" r={r} fill="none" stroke={c} strokeWidth="12"
          strokeDasharray={i % 2 ? '10 6' : '22 8'} />
      ))}
    </svg>
  );
}

export default function DiwaliBanner({ pct = 90, variant = 'compact', children }) {
  return (
    <section className={`db db-${variant}`} aria-label={`Big Diwali Sale — up to ${pct}% off`}>
      <div className="db-stage">
        <div className="db-sky" aria-hidden="true" />
        <FireworksCanvas density={1.4} power={1.1} rays speed={0.5} />
        <Skyline />
        <div className="db-bokeh" aria-hidden="true" />
        <div className="db-floor" aria-hidden="true" />
        <Rangoli />

        <div className="db-crackers" aria-hidden="true"><CrackerScene full /></div>

        <Garland />
        <Lantern />

        <BrandMark />

        {/* ornate frame */}
        <div className="db-frame">
          <svg viewBox="0 0 400 440" className="db-frame-svg" aria-hidden="true">
            <defs>
              <radialGradient id="dbFrameFill" cx=".5" cy=".45" r=".65">
                <stop offset="0" stopColor="#b0126a" /><stop offset=".7" stopColor="#7a0c4f" /><stop offset="1" stopColor="#4a0630" />
              </radialGradient>
              <filter id="dbBulbGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <path d={FRAME} fill="url(#dbFrameFill)" stroke="#f6c453" strokeWidth="5" />
            <path d={FRAME} fill="none" stroke="#f6c453" strokeWidth="1.5" opacity=".7"
              transform="translate(200 220) scale(.9) translate(-200 -220)" />
            <g filter="url(#dbBulbGlow)">
              <path d={FRAME} fill="none" stroke="#fff3c4" strokeWidth="7" strokeLinecap="round" strokeDasharray="0 18" className="db-bulbs-a" />
              <path d={FRAME} fill="none" stroke="#ffd166" strokeWidth="7" strokeLinecap="round" strokeDasharray="0 18" strokeDashoffset="9" className="db-bulbs-b" />
            </g>
          </svg>
          <Diya className="db-frame-diya" />
          <div className="db-frame-text">
            <span className="db-big">BIG</span>
            <span className="db-script db-diwali">Diwali</span>
            <span className="db-script db-sale">Sale</span>
            <span className="db-tag">BRIGHTER CELEBRATIONS<br />BIGGER SAVINGS</span>
          </div>
        </div>

        {/* starburst */}
        <div className="db-badge">
          <svg viewBox="0 0 220 220" aria-hidden="true">
            <defs>
              <radialGradient id="dbStar"><stop offset="0" stopColor="#fff6c7" /><stop offset=".6" stopColor="#ffd23f" /><stop offset="1" stopColor="#f4a300" /></radialGradient>
            </defs>
            <g className="db-badge-spin">
              <polygon points={STAR} fill="url(#dbStar)" stroke="#e09400" strokeWidth="3" />
            </g>
            <circle cx="110" cy="110" r="76" fill="none" stroke="#c77d00" strokeWidth="2" strokeDasharray="5 5" />
            <circle cx="110" cy="110" r="92" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeDasharray="0 16" className="db-bulbs-a" />
          </svg>
          <div className="db-badge-text">
            <span className="db-upto">UPTO</span>
            <span className="db-pct">{pct}%</span>
            <span className="db-off">OFF</span>
          </div>
        </div>

        {/* floor lamps */}
        <Bowl />
        <div className="db-diyas" aria-hidden="true">
          {[0, 1, 2, 3].map(i => <Diya key={i} style={{ animationDelay: `${i * .2}s` }} />)}
        </div>
      </div>

      {children && <div className="db-cta">{children}</div>}
    </section>
  );
}
